/**
 * Application entry point
 */

import { loadPubData, getPubStats } from './pubData.js';
import { initializeMap } from './map.js';
import { createPubMarkers } from './markers.js';
import { loadPreferences } from './preferences.js';
import { ClusterManager } from './clusterManager.js';
import { ClusterControls } from './controls.js';
import { LocationManager } from './locationManager.js';
import { LocationControl } from './locationControl.js';
import { loadLocationPreferences } from './locationPreferences.js';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import './styles.css';

/**
 * Shows the loading spinner
 */
function showLoading(): void {
  const loadingEl = document.getElementById('loading');
  if (loadingEl) {
    loadingEl.classList.remove('hidden');
  }
}

/**
 * Hides the loading spinner
 */
function hideLoading(): void {
  const loadingEl = document.getElementById('loading');
  if (loadingEl) {
    loadingEl.classList.add('hidden');
  }
}

/**
 * Shows an error message to the user
 * @param message - Error message to display
 */
function showError(message: string): void {
  hideLoading();

  const errorEl = document.getElementById('error');
  const errorMessageEl = document.getElementById('error-message');

  if (errorEl && errorMessageEl) {
    errorMessageEl.textContent = message;
    errorEl.classList.remove('hidden');
  }

  console.error('Application error:', message);
}

/**
 * Updates the header with statistics
 * @param stats - Pub statistics object
 */
function updateHeader(stats: ReturnType<typeof getPubStats>): void {
  const headerEl = document.querySelector('header p');
  if (headerEl) {
    headerEl.textContent = `${stats.visited} / ${stats.total} visited (${stats.visitedPercentage}%) • ${stats.closed} closed`;
  }
}

/**
 * Main application initialization
 */
async function initializeApp(): Promise<void> {
  try {
    showLoading();
    console.log('Initializing Wetherspoons Pub Tracker...');

    // Load pub data
    const pubs = await loadPubData();

    // Calculate statistics
    const stats = getPubStats(pubs);
    console.log('Pub statistics:', stats);
    // Update header with stats
    updateHeader(stats);

    // Initialize map
    const map = initializeMap('map');

    // Load user preferences
    const preferences = loadPreferences();
    console.log('User preferences loaded:', preferences);

    // Initialize cluster manager with the pub markers
    const clusterManager = new ClusterManager(map, createPubMarkers(pubs));
    clusterManager.initialize(preferences.clusteringEnabled);

    // Add cluster controls to map
    const controls = new ClusterControls(preferences, clusterManager, {
      position: 'topright',
    });
    map.addControl(controls);

    // Initialize location feature
    const locationPrefs = loadLocationPreferences();
    const locationManager = new LocationManager(map);
    const locationControl = new LocationControl(locationManager, locationPrefs, {
      position: 'topleft',
    });
    map.addControl(locationControl);

    // Auto-restore location if previously enabled
    if (locationPrefs.locationEnabled) {
      locationManager.start(
        true,
        (err) => {
          // On auto-restore error (e.g. permission revoked), correct the stored preference
          console.warn('Location auto-restore failed:', err.userMessage);
          locationPrefs.locationEnabled = false;
        }
      );
    }

    // Hide loading spinner
    hideLoading();

    console.log('Application initialized successfully with cluster and location controls!');
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'An unknown error occurred while loading the application.';

    showError(errorMessage);
  }
}

// Start the application when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  // DOM already loaded
  initializeApp();
}
